import React, { useState, createContext, useContext, useEffect } from 'react';
import './style.css';
import ViewProjects from './ViewProjects/ViewProjects';
import AboutProjects from './AboutProjects/AboutProjects';
import ProjectsPayoffSimulator from './ProjectsPayoffSimulator/ProjectsPayoffSimulator';
import ProjectCard from '/src/Components/ProjectCard/ProjectCard';
import CreateRefinedProjectsList from '/src/services/CreateRefinedProjectsList';
import HowToUseProjects from './HowToUseProjects/HowToUseProjects';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import WhatIfMachinePlanSummary from '/src/Components/WhatIfMachinePlanSummary/WhatIfMachinePlanSummary';
import { usePropertyGaLogging } from '/src/contexts/PropertyGaLoggingContext';
// import ProjectsSummaryData from '/src/Components/ProjectsSummaryData/ProjectsSummaryData';

// Import the AreaDataContext
import { AreaDataContext } from '../PropertyExplorer';

// Create the context
export const ProjectsPayoffContext = createContext();

const YourOptions = ({ propertyData }) => {
    const { deviceType } = useContext(WindowSizeContext);
    const { handleLoggingGaEventFromProperty } = usePropertyGaLogging();

    const { selectedProject, setSelectedProject, handleSectionChange, handleUrlSubsectionChange, handleRevertToPreviousUrl, handleRevertToPreviousSubsectionUrl } = useContext(AreaDataContext);
    const { handleExplainerViewed, user, LOG_GA_EVENT } = useContext(AddressSearchContext);
    const addressId = propertyData?.address_id || null;

    // Load 'wim' data from local storage and parse it
    const wimData = localStorage.getItem('wim');
    const wimDataJson = JSON.parse(wimData) || {};

    // Get the set for this address from wimDataJson, or create an empty set if it doesn't exist
    const wimSet = new Set(wimDataJson[addressId] || []);

    // Set the different section names / slugs 
    const whatWeWouldDoSlug = 'what-we-would-do';
    const helpMeChooseSlug = 'help-me-choose';
    const aboutSlug = 'about';
    const browseSlug = 'browse-projects';
    const inComboLabSlug = 'in-combo-lab';
    const comboLabSlug = 'combo-lab';
    const howToNavigateProjectsSlug = 'how-to-navigate-projects';

    const projectViewOptions = [
        whatWeWouldDoSlug,
        helpMeChooseSlug,
        browseSlug,
        inComboLabSlug,
        comboLabSlug
    ]

    // Determine if first view should be about or jump right to browse, based on user.projectsExplainerViewed
    const firstView = user.projectsExplainerViewed ? whatWeWouldDoSlug : aboutSlug;

    // console.log('firstView', firstView, user,  user.projectsExplainerViewed);

    const [selectedSection, setSelectedSection] = useState(firstView); // Which page is currently selected
    const [costsAndPayoffTotals, setCostsAndPayoffTotals] = useState(null); // The totals for the costs and payoff
    const [userSelectedSimulatorProjectIds, setUserSelectedSimulatorProjectIds] = useState(wimSet); // The ids of the projects that the user has selected in the simulator
    const [selectedProjectsList, setSelectedProjectsList] = useState([]); // The projects that the user has selected
    const [notSelectedProjectsList, setNotSelectedProjectsList] = useState([]); // The projects that the user has not selected
    const [refinedProjectsList, setRefinedProjectsList] = useState([]); // The projects that are displayed in the projects list
    const [showLab, setShowLab] = useState(false);
    const [showHowToNavigate, setShowHowToNavigate] = useState(false);
    // const [selectedProject, setSelectedProject] = useState(null); // The project that is currently selected


    // useEffect to monitor the selectedSection and call the handleSectionChange function
    useEffect(() => {
        if (selectedSection) {
            handleUrlSubsectionChange(selectedSection);
        }
    }, [selectedSection]);


    // Get property areas threatened, projects list, number of projects, total discounts, and climate risks
    const property_areas_threatened = propertyData?.data?.property_areas_threatened?.house_categories_with_risks || [];
    const projects_list = propertyData?.data?.recommended_projects?.projects_list || [];
    const climate_risks = propertyData?.data?.climate_risks || [];


    // Function to calculate the totals
    const calculateTotals = (projects_list) => {

        // Get the total count of projects
        const total_project_count = projects_list.length;

        // Calculate all the totals
        const total_capital_freed_by_year = projects_list.reduce((acc, project) => {
            return acc.map((value, index) => value + project.project_payoff_by_scenario[0].by_value_type.capital_freed.by_year[index]);
        }, Array(10).fill(0));

        const total_opex_insurance_savings_by_year = projects_list.reduce((acc, project) => {
            return acc.map((value, index) => value + project.project_payoff_by_scenario[0].by_value_type.opex.insurance.by_year[index]);
        }, Array(10).fill(0));

        const total_opex_maintenance_savings_by_year = projects_list.reduce((acc, project) => {
            return acc.map((value, index) => value + project.project_payoff_by_scenario[0].by_value_type.opex.maintenance.by_year[index]);
        }, Array(10).fill(0));

        const total_opex_utilities_savings_by_year = projects_list.reduce((acc, project) => {
            return acc.map((value, index) => value + project.project_payoff_by_scenario[0].by_value_type.opex.utilities.by_year[index]);
        }, Array(10).fill(0));

        const total_opex_taxes_savings_by_year = projects_list.reduce((acc, project) => {
            return acc.map((value, index) => value + project.project_payoff_by_scenario[0].by_value_type.opex.taxes.by_year[index]);
        }, Array(10).fill(0));

        const total_opex_savings_by_year = total_opex_insurance_savings_by_year.map((value, index) =>
            value +
            total_opex_maintenance_savings_by_year[index] +
            total_opex_utilities_savings_by_year[index] +
            total_opex_taxes_savings_by_year[index]
        );

        const total_property_value_upside_by_year = projects_list.reduce((acc, project) => {
            return acc.map((value, index) => value + project.project_payoff_by_scenario[0].by_value_type.property_value_upside.by_year[index]);
        }, Array(10).fill(0));

        const output = {
            capital_freed: total_capital_freed_by_year,
            opex: {
                insurance: total_opex_insurance_savings_by_year,
                maintenance: total_opex_maintenance_savings_by_year,
                utilities: total_opex_utilities_savings_by_year,
                taxes: total_opex_taxes_savings_by_year,
                total: total_opex_savings_by_year
            },
            property_value_upside: total_property_value_upside_by_year
        }

        return output;
    }


    // Create a handler that updates the selected project when a new project is clicked (or to null if the same project is clicked again)
    const handleProjectClick = (projectId) => {
        // Get the project from the selectedArea.projects_list array
        const project = refinedProjectsList.find(project => project.project_id === projectId);

        // If selectedProject.id is not the same as projectId, set the selected project to the project
        if (selectedProject?.id !== projectId) {
            setSelectedProject(project);
        }
        // If selectedProject.id is the same as projectId, set the selected project to null
        else {
            setSelectedProject(null);
        }
    };

    // Create a handler that sets the selected project to null when the close button is clicked or clicked outside of the project card
    const handleCloseProjectClick = () => {
        setSelectedProject(null);
    }

    // Create a handler that sets the selected section to 'how-to-use-projects' when the explore projects button is clicked
    const handleExploreProjectsClick = () => {
        // setSelectedSection('how-to-use-projects');
        setSelectedSection(whatWeWouldDoSlug);
        handleUrlSubsectionChange(whatWeWouldDoSlug);
        setShowHowToNavigate(true);
    }

    // Create a handler that toggles setShowHowToNavigate on and updates the subsection url
    const handleToggleHowToNavigateOn = () => {
        setShowHowToNavigate(true);
        handleUrlSubsectionChange(howToNavigateProjectsSlug);
    }

    // Create a handler that sets the selected section to 'browse' when the explore projects button is clicked
    const handleHowToUseProjectsSectionCompleteClick = () => {
        handleExplainerViewed('projectsExplainerViewed');
        setShowHowToNavigate(false);
        handleRevertToPreviousSubsectionUrl();
    }

    // UseEffect to monitor for propertyData changes, create the refined projects list, run the calculateTotals function, and update the userSelectedSimulatorProjectIds
    useEffect(() => {
        if (propertyData) {
            const projects_list = propertyData?.data?.recommended_projects?.projects_list || [];
            // const refinedProjectsList = createRefinedProjectsList(projects_list);
            const refinedProjectsList = CreateRefinedProjectsList(projects_list, propertyData);
            // console.log(refinedProjectsList);
            setRefinedProjectsList(refinedProjectsList);
            const totals = calculateTotals(refinedProjectsList);
            setCostsAndPayoffTotals(totals);
            // setUserSelectedSimulatorProjectIds(new Set());
        }
    }, [propertyData]);

    // UseEffect to update the selected and not selected projects lists whenever the userSelectedSimulatorProjectIds changes
    useEffect(() => {
        if (!propertyData) return;

        const projects_list = refinedProjectsList;
        const selectedProjectsList = projects_list.filter(project => userSelectedSimulatorProjectIds.has(project.project_id));
        setSelectedProjectsList(selectedProjectsList);

        const notSelectedProjectsList = projects_list.filter(project => !userSelectedSimulatorProjectIds.has(project.project_id));
        setNotSelectedProjectsList(notSelectedProjectsList);
    }, [userSelectedSimulatorProjectIds, refinedProjectsList]);

    // Create a handler function that takes in a project id and adds it to the userSelectedSimulatorProjects set
    const handleUserAddingSimulatorProjectId = (projectIdInput) => {
        // Convert input to array if it's a single string
        const projectIds = Array.isArray(projectIdInput) ? projectIdInput : [projectIdInput];

        // Get the count of the projects that are being added
        const projectCount = projectIds.length;

        // Log a single GA event with all project IDs
        const gaAdditionalParams = {
            projects_added_to_lab: projectIds,
            projects_added_to_lab_count: projectCount
        }
        handleLoggingGaEventFromProperty('recommendations_project_added_to_lab', gaAdditionalParams);

        // Update state by adding all project IDs to the set
        setUserSelectedSimulatorProjectIds(prev => {
            const newSet = new Set(prev);
            projectIds.forEach(id => newSet.add(id));
            return newSet;
        });

        // Get current wim data from localStorage
        const currentWimData = JSON.parse(localStorage.getItem('wim')) || {};

        // Update the set for this addressId while preserving other addresses
        // Combine existing IDs with new IDs, ensuring uniqueness through Set
        currentWimData[addressId] = [...new Set([
            ...(currentWimData[addressId] || []),
            ...projectIds
        ])];

        // Save back to localStorage
        localStorage.setItem('wim', JSON.stringify(currentWimData));
    }

    // Create a handler function that takes in a project id and removes it from the userSelectedSimulatorProjects set
    const handleUserRemovingSimulatorProjectId = (projectId) => {
        setUserSelectedSimulatorProjectIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(projectId);

            // Get current wim data from localStorage
            const currentWimData = JSON.parse(localStorage.getItem('wim')) || {};

            // Update the set for this addressId while preserving other addresses
            currentWimData[addressId] = [...newSet];

            // Save back to localStorage
            localStorage.setItem('wim', JSON.stringify(currentWimData));

            return newSet;
        });
    }

    // Create a handler that clears the userSelectedSimulatorProjectIds
    const handleClearSimulator = () => {
        setUserSelectedSimulatorProjectIds(new Set([]));

        // Load the 'wim' data from local storage
        const currentWimData = JSON.parse(localStorage.getItem('wim')) || {};

        // Update the set for this addressId while preserving other addresses
        currentWimData[addressId] = [];

        // Save back to localStorage
        localStorage.setItem('wim', JSON.stringify(currentWimData));
    }

    const handleLabToggle = () => {

        const isShowLabCurrentlyShowing = showLab;

        setShowLab(!showLab);
        if (!isShowLabCurrentlyShowing) {
            handleUrlSubsectionChange(comboLabSlug);
            handleLoggingGaEventFromProperty('projects_lab_opened');
        } else {
            handleRevertToPreviousSubsectionUrl();
        }
    }


    return (
        <ProjectsPayoffContext.Provider
            value={{
                costsAndPayoffTotals,
                userSelectedSimulatorProjectIds,
                handleUserAddingSimulatorProjectId,
                handleUserRemovingSimulatorProjectId,
                selectedProjectsList,
                setSelectedProjectsList,
                notSelectedProjectsList,
                refinedProjectsList,
                selectedProject,
                handleProjectClick,
                handleCloseProjectClick,
                handleExploreProjectsClick,
                setSelectedSection,
                handleClearSimulator,
                showLab,
                handleLabToggle,
                selectedSection,
                whatWeWouldDoSlug,
                helpMeChooseSlug,
                browseSlug,
                inComboLabSlug,
                comboLabSlug
            }}>

            <div className="risk-overview-individual-section-container">

                {showHowToNavigate && (
                    <HowToUseProjects
                        projects={refinedProjectsList}
                        handleExploreProjectsClick={handleHowToUseProjectsSectionCompleteClick}
                    />
                )}
                <div className={`taking-action-about-container ${deviceType}`}>
                    {selectedSection === aboutSlug && (
                        <AboutProjects
                            propertyData={propertyData}
                            handleExploreProjectsClick={handleExploreProjectsClick}
                        />
                    )}
                    {projectViewOptions.includes(selectedSection) && (
                        <ViewProjects
                            propertyData={propertyData}
                            setShowHowToNavigate={setShowHowToNavigate}
                            handleToggleHowToNavigateOn={handleToggleHowToNavigateOn}
                        />
                    )}
                    {/* {selectedSection === 'simulator' && <ProjectsPayoffSimulator propertyData={propertyData} />} */}
                </div>
              
            </div>
        </ProjectsPayoffContext.Provider>
    );
};

export default YourOptions;

